import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styles from './SearchBar.module.css';

const QUICK_PICKS = ['AAPL', 'MSFT', 'TSLA', 'NVDA', 'COST', 'AMZN', 'HLAL', 'QQQ', 'SPY', 'ISWD'];

export default function SearchBar({ onScreen, loading, activeTicker = '' }) {
  const { t } = useTranslation();
  const [ticker,      setTicker]      = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop,    setShowDrop]    = useState(false);
  const [activeIdx,   setActiveIdx]   = useState(-1);

  const debounceRef    = useRef(null);
  const wrapRef        = useRef(null);
  const suppressRef    = useRef(false); // true right after a suggestion is selected

  // Sync input when a screen is triggered externally (e.g. Related Halal cards)
  useEffect(() => {
    if (activeTicker && activeTicker !== ticker) {
      suppressRef.current = true;
      setTicker(activeTicker);
      setShowDrop(false);
      setSuggestions([]);
    }
  }, [activeTicker]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch suggestions (debounced 300ms) ──────────────────────────────
  useEffect(() => {
    const q = ticker.trim();

    if (suppressRef.current) {
      suppressRef.current = false; // consume the flag, skip this fetch
      return;
    }

    if (!q) { setSuggestions([]); setShowDrop(false); return; }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/search', { params: { q } });
        const results = data.results || [];
        setSuggestions(results);
        setShowDrop(results.length > 0);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
        setShowDrop(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [ticker]);

  // ── Close on outside click ────────────────────────────────────────────
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function selectSuggestion(symbol) {
    suppressRef.current = true;   // prevent next ticker-change effect from fetching
    setTicker(symbol);
    setSuggestions([]);
    setShowDrop(false);
    setActiveIdx(-1);
    onScreen(symbol);
  }

  function submit() {
    const t = ticker.trim().toUpperCase();
    if (!t) return;
    suppressRef.current = true;
    setShowDrop(false);
    setSuggestions([]);
    onScreen(t);
  }

  function handleKey(e) {
    if (!showDrop) {
      if (e.key === 'Enter') submit();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        selectSuggestion(suggestions[activeIdx].symbol);
      } else {
        submit();
      }
    } else if (e.key === 'Escape') {
      setShowDrop(false);
      setActiveIdx(-1);
    }
  }

  return (
    <div className={styles.block}>
      <div className={styles.row} ref={wrapRef}>
        <div className={styles.inputWrap}>
          <span className={styles.dollar}>$</span>
          <input
            className={styles.input}
            type="text"
            value={ticker}
            onChange={e => setTicker(e.target.value.toUpperCase())}
            onKeyDown={handleKey}
            onFocus={() => suggestions.length > 0 && setShowDrop(true)}
            placeholder={t('search.placeholder')}
            maxLength={20}
            autoCapitalize="characters"
            autoComplete="off"
            disabled={loading}
          />
          {showDrop && suggestions.length > 0 && (
            <div className={styles.dropdown}>
              {suggestions.map((s, i) => (
                <div
                  key={s.symbol}
                  className={`${styles.dropItem} ${i === activeIdx ? styles.dropItemActive : ''}`}
                  onMouseDown={() => selectSuggestion(s.symbol)}
                >
                  <span className={styles.dropSymbol}>{s.symbol}</span>
                  <span className={styles.dropName}>{s.name}</span>
                  <span className={styles.dropMeta}>
                    {s.exchange && <span>{s.exchange}</span>}
                    {s.type && <span className={styles.dropType}>{t(`search.type_labels.${s.type}`) || s.type}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          className={styles.btn}
          onClick={submit}
          disabled={loading || !ticker.trim()}
        >
          {loading ? t('search.button_loading') : t('search.button_screen')}
        </button>
      </div>

      <div className={styles.picks}>
        {QUICK_PICKS.map(t => (
          <button
            key={t}
            className={styles.pick}
            onClick={() => { suppressRef.current = true; setTicker(t); setShowDrop(false); onScreen(t); }}
            disabled={loading}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}
